import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Alert,
  Divider,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { Form, Formik, FormikProps } from 'formik';
import { getStates } from '../../../services/states.service';
import { State } from '../../../models/state.model';
import { getQuote, createQuote, updateQuote, getQuoteAuditLog } from '../../../services/quotes.service';
import type { QuoteAuditEntry } from '../../../models/quoteAudit.model';
import { hasPermission, Permission } from '../../../models/role.model';
import { Quote } from '../../../models/quote.model';
import { CreateQuote } from '../../../models/createQuote.model';
import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { isDev } from '../../../utils/utils';
import { normalizeQuoteValues } from '../../../utils/quote.util';
import { calculateFees, describeConditions, snapshotFees, QUOTE_COLLATERAL_TYPES } from '../../../utils/fee-calculator.util';
import { formatMoney } from '../../../utils/format.util';
import { quoteInitialValues, QuoteSchemaYup } from './validationSchema';

function formatAuditTimestamp(ts: number): string {
  return new Date(ts).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function auditEntrySummary(entry: QuoteAuditEntry): string {
  const who = entry.actorDisplayName || 'Someone';
  const when = formatAuditTimestamp(entry.timestamp);
  switch (entry.action) {
    case 'created':
      return `Quote was created by ${who} on ${when}`;
    case 'deleted':
      return `${who} deleted this quote on ${when}`;
    case 'updated':
      if (entry.changedFields && entry.changedFields.length > 0) {
        const fields = entry.changedFields.join(', ');
        return `${who} updated ${fields} on ${when}`;
      }
      return `${who} updated this quote on ${when}`;
    default:
      return `${who} — ${when}`;
  }
}

function formatChangeValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val !== 'object') return String(val);
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

function auditEntryChanges(entry: QuoteAuditEntry): ReactNode {
  if (entry.action !== 'updated' || !entry.changes || Object.keys(entry.changes).length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
      {Object.entries(entry.changes).map(([field, { from, to }]) => (
        <li key={field}>
          <Typography variant="body2" component="span" color="text.secondary">
            {field}: {formatChangeValue(from)} → {formatChangeValue(to)}
          </Typography>
        </li>
      ))}
    </Box>
  );
}


export default function QuoteFormContainer() {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const { isAuthenticated, currentUser } = useAppContext();
  const [tabValue, setTabValue] = useState(0);
  const [auditLog, setAuditLog] = useState<QuoteAuditEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<Quote>>(quoteInitialValues);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [states, setStates] = useState<State[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesError, setStatesError] = useState<string | null>(null);
  const [availableCounties, setAvailableCounties] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Load states from API on component mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        setStatesLoading(true);
        setStatesError(null);
        const statesData = await getStates();
        setStates(statesData);
      } catch (err) {
        console.error('Error loading states:', err);
        setStatesError('Failed to load states. Please try again.');
      } finally {
        setStatesLoading(false);
      }
    };

    loadStates();
  }, []);

  // Load quote data if editing
  useEffect(() => {
    const loadQuote = async () => {
      if (!isAuthenticated || !quoteId || quoteId === 'new') {
        setIsEditMode(false);
        setIsLoading(false);
        return;
      }

      setIsEditMode(true);
      setIsLoading(true);
      try {
        const quoteData = await getQuote(quoteId);
        setFormData(quoteData);
      } catch (err) {
        console.error('Error loading quote:', err);
        onError(err);
        setError('Failed to load quote. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, isAuthenticated]);

  const showActivityTab = Boolean(quoteId && quoteId !== 'new');
  const canViewAuditLog = currentUser && hasPermission(currentUser, Permission.VIEW_QUOTE_LOGS);
  const showActivityTabWithPermission = Boolean(showActivityTab && canViewAuditLog && isEditMode);

  useEffect(() => {
    if (!showActivityTabWithPermission || quoteId === undefined || tabValue !== 1) return;
    let cancelled = false;
    setAuditLogLoading(true);
    getQuoteAuditLog(quoteId, { limit: 50 })
      .then((res) => {
        if (!cancelled) setAuditLog(res.items);
      })
      .catch((e) => {
        if (!cancelled) onError(e);
      })
      .finally(() => {
        if (!cancelled) setAuditLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, showActivityTabWithPermission, tabValue]);

  const handleRetryLoadStates = () => {
    const loadStates = async () => {
      try {
        setStatesLoading(true);
        setStatesError(null);
        const statesData = await getStates();
        setStates(statesData);
      } catch (err) {
        console.error('Error loading states:', err);
        setStatesError('Failed to load states. Please try again.');
      } finally {
        setStatesLoading(false);
      }
    };

    loadStates();
  };

  // Update counties when state changes - this will be handled inside Formik now
  // Keep this for the availableCounties state
  const updateAvailableCounties = (stateAbbrev: string | undefined) => {
    if (stateAbbrev) {
      const selectedState = states.find((state) => state.abbreviation === stateAbbrev);
      if (selectedState) {
        const countyNames = selectedState.counties.map((county) => county.name);
        setAvailableCounties(countyNames);
      } else {
        setAvailableCounties([]);
      }
    } else {
      setAvailableCounties([]);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (rawValues: Partial<Quote>) => {
    const normalized = normalizeQuoteValues(rawValues);
    const values = {
      ...normalized,
      fees: snapshotFees(
        states.find((st) => st.abbreviation === normalized.titleState),
        normalized,
      ),
    };
    // Validate that states are loaded
    if (statesLoading || states.length === 0) {
      setError('Please wait for states to load before submitting.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isEditMode && quoteId) {
        const { id, createdAt, updatedAt, createdBy, ...updateData } = values;
        await updateQuote(quoteId, updateData as CreateQuote);
        showSnackbar('Quote updated successfully!', 'success');
      } else {
        const { id, createdAt, updatedAt, createdBy, ...createData } = values;
        const savedQuote = await createQuote(createData as CreateQuote);
        showSnackbar('Quote created successfully!', 'success');
        navigate(`/quotes/${savedQuote.id}`);
      }
    } catch (err) {
      onError(err);
      setError(isEditMode ? 'Failed to update quote. Please try again.' : 'Failed to create quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {statesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        <Formik initialValues={formData} validationSchema={QuoteSchemaYup} onSubmit={handleSubmit} enableReinitialize>
          {({ handleChange, handleBlur, values, touched, errors, setFieldValue }: FormikProps<Partial<Quote>>) => {
            // Update available counties when state changes
            const currentState = values.titleState;
            if (currentState && currentState !== formData.titleState) {
              updateAvailableCounties(currentState);
            }
            // Sync formData for print template
            setFormData(values);

            return (
              <>
                <Form noValidate style={{ display: statesLoading ? 'none' : 'block' }}>
                  {showActivityTabWithPermission ? (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tabs value={tabValue} onChange={(_e, v) => setTabValue(v)} aria-label="quote form tabs">
                        <Tab label="Quote" id="quote-tab-0" />
                        <Tab label="Activity" id="quote-tab-1" />
                      </Tabs>
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      display: showActivityTabWithPermission && tabValue === 1 ? 'none' : 'block',
                    }}
                  >
                  {/* Header Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h2" gutterBottom>
                      {isEditMode ? 'Edit Quote' : 'Create Quote'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                  </Box>

                  {/* Status Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Status
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth error={touched.status && Boolean(errors.status)}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            name="status"
                            id="status"
                            value={values.status || ''}
                            label="Status"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value="">Not Sent</MenuItem>
                            <MenuItem value="Sent to DMV">Sent to DMV</MenuItem>
                            <MenuItem value="Sent to Driver">Sent to Driver</MenuItem>
                          </Select>
                          {touched.status && errors.status && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {errors.status}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Quote Information Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Quote Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {statesError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {statesError}
                        <Button size="small" onClick={handleRetryLoadStates} sx={{ ml: 2 }} disabled={statesLoading}>
                          Retry
                        </Button>
                      </Alert>
                    )}

                    <Grid container spacing={3}>
                      {isEditMode && values.id && (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Quote ID"
                            value={values.id || ''}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Date"
                          name="date"
                          id="date"
                          type="date"
                          value={values.date || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.date && Boolean(errors.date)}
                          helperText={touched.date && errors.date}
                          slotProps={{
                            inputLabel: { shrink: true },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="driverLastName"
                          id="driverLastName"
                          value={values.driverLastName || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.driverLastName && Boolean(errors.driverLastName)}
                          helperText={touched.driverLastName && errors.driverLastName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="VIN Number"
                          name="vinNumber"
                          id="vinNumber"
                          value={values.vinNumber || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.vinNumber && Boolean(errors.vinNumber)}
                          helperText={touched.vinNumber && errors.vinNumber}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Zip Code"
                          name="titleZip"
                          id="titleZip"
                          value={values.titleZip || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.titleZip && Boolean(errors.titleZip)}
                          helperText={touched.titleZip && errors.titleZip}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth error={touched.titleState && Boolean(errors.titleState)}>
                          <InputLabel>State</InputLabel>
                          <Select
                            name="titleState"
                            id="titleState"
                            value={values.titleState || ''}
                            label="State"
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue('county', ''); // Reset county when state changes
                            }}
                            onBlur={handleBlur}
                            disabled={statesLoading}
                            endAdornment={statesLoading ? <CircularProgress size={20} /> : undefined}
                          >
                            <MenuItem value="">Choose</MenuItem>
                            {statesLoading ? (
                              <MenuItem value="">Loading states...</MenuItem>
                            ) : (
                              states.map((state) => (
                                <MenuItem key={state.abbreviation} value={state.abbreviation}>
                                  {state.name}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          {touched.titleState && errors.titleState && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {errors.titleState}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth error={touched.county && Boolean(errors.county)}>
                          <InputLabel>County</InputLabel>
                          <Select
                            name="county"
                            id="county"
                            value={values.county || ''}
                            label="County"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!values.titleState || statesLoading}
                          >
                            <MenuItem value="">Choose</MenuItem>
                            {!values.titleState ? (
                              <MenuItem value="">Select a state first</MenuItem>
                            ) : statesLoading ? (
                              <MenuItem value="">Loading counties...</MenuItem>
                            ) : availableCounties.length === 0 ? (
                              <MenuItem value="">No counties available</MenuItem>
                            ) : (
                              availableCounties.map((county) => (
                                <MenuItem key={county} value={county}>
                                  {county}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          {touched.county && errors.county && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {errors.county}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="City"
                          name="city"
                          id="city"
                          value={values.city || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Transaction Type"
                          name="transactionType"
                          id="transactionType"
                          value={values.transactionType || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="outsideCityLimits"
                              id="outsideCityLimits"
                              checked={values.outsideCityLimits || false}
                              onChange={handleChange}
                            />
                          }
                          label="Outside City Limits - Yes"
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                          <InputLabel>What is the collateral?</InputLabel>
                          <Select
                            name="collateralType"
                            id="collateralType"
                            value={values.collateralType || ''}
                            label="What is the collateral?"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value="">Choose</MenuItem>
                            {QUOTE_COLLATERAL_TYPES.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Service Form URL Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Service Form URL
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Example:</strong> https://get-started.collectorcarleaders.com/service-forms/aax78h6ukg
                    </Typography>
                    <TextField
                      fullWidth
                      name="serviceFormURL"
                      id="serviceFormURL"
                      value={values.serviceFormURL || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.serviceFormURL && Boolean(errors.serviceFormURL)}
                      helperText={touched.serviceFormURL && errors.serviceFormURL}
                      sx={{ maxWidth: 600 }}
                    />
                  </Box>

                  {/* Driver Info Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Driver Info
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, lg: 4 }}>
                        <TextField
                          fullWidth
                          label="Driver Last Name"
                          name="driverLastName"
                          id="driverLastName"
                          value={values.driverLastName || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.driverLastName && Boolean(errors.driverLastName)}
                          helperText={touched.driverLastName && errors.driverLastName}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Vehicle Info Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Vehicle Info
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Column 1 */}
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              label="Vin"
                              name="vinNumber"
                              id="vinNumber"
                              value={values.vinNumber || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.vinNumber && Boolean(errors.vinNumber)}
                              helperText={touched.vinNumber && errors.vinNumber}
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Is Commercial Use</FormLabel>
                              <RadioGroup
                                row
                                name="commercialUse"
                                value={values.commercialUse || 'No'}
                                onChange={handleChange}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="Model Year"
                              name="modelYear"
                              id="modelYear"
                              value={values.modelYear || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.modelYear && Boolean(errors.modelYear)}
                              helperText={touched.modelYear && errors.modelYear}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="Make"
                              name="make"
                              id="make"
                              value={values.make || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.make && Boolean(errors.make)}
                              helperText={touched.make && errors.make}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="Body Style"
                              name="bodyStyle"
                              id="bodyStyle"
                              value={values.bodyStyle || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.bodyStyle && Boolean(errors.bodyStyle)}
                              helperText={touched.bodyStyle && errors.bodyStyle}
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              label="Odometer"
                              name="odometer"
                              id="odometer"
                              value={values.odometer || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.odometer && Boolean(errors.odometer)}
                              helperText={touched.odometer && errors.odometer}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              label="Registration Months"
                              name="registrationMonths"
                              id="registrationMonths"
                              value={values.registrationMonths || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.registrationMonths && Boolean(errors.registrationMonths)}
                              helperText={touched.registrationMonths && errors.registrationMonths}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Column 2 */}
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              fullWidth
                              label="Weight"
                              name="weight"
                              id="weight"
                              value={values.weight || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.weight && Boolean(errors.weight)}
                              helperText={touched.weight && errors.weight}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 6 }}>
                            <TextField
                              fullWidth
                              label="Registered"
                              name="registeredWeight"
                              id="registeredWeight"
                              value={values.registeredWeight || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.registeredWeight && Boolean(errors.registeredWeight)}
                              helperText={touched.registeredWeight && errors.registeredWeight}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 6 }}>
                            <TextField
                              fullWidth
                              label="GVW"
                              name="gvw"
                              id="gvw"
                              value={values.gvw || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.gvw && Boolean(errors.gvw)}
                              helperText={touched.gvw && errors.gvw}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 6 }}>
                            <TextField
                              fullWidth
                              label="GVWR"
                              name="gvwr"
                              id="gvwr"
                              value={values.gvwr || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.gvwr && Boolean(errors.gvwr)}
                              helperText={touched.gvwr && errors.gvwr}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                              <InputLabel>Fuel Type</InputLabel>
                              <Select
                                name="fuelType"
                                id="fuelType"
                                value={values.fuelType || ''}
                                label="Fuel Type"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <MenuItem value="">Choose</MenuItem>
                                <MenuItem value="Gasoline">Gasoline</MenuItem>
                                <MenuItem value="Diesel">Diesel</MenuItem>
                                <MenuItem value="Electric">Electric</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">EV Tax Credit?</FormLabel>
                              <RadioGroup
                                row
                                name="evTaxCredit"
                                value={values.evTaxCredit || 'Do not apply'}
                                onChange={handleChange}
                              >
                                <FormControlLabel
                                  value="Apply credit to taxable amount"
                                  control={<Radio />}
                                  label="Apply credit to taxable amount"
                                />
                                <FormControlLabel value="Do not apply" control={<Radio />} label="Do not apply" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              label="Tax Credit Amount"
                              name="taxCreditAmount"
                              id="taxCreditAmount"
                              value={values.taxCreditAmount || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.taxCreditAmount && Boolean(errors.taxCreditAmount)}
                              helperText={touched.taxCreditAmount && errors.taxCreditAmount}
                              slotProps={{
                                input: {
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="Length"
                              name="length"
                              id="length"
                              value={values.length || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.length && Boolean(errors.length)}
                              helperText={touched.length && errors.length}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Transaction Info Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Transaction Info
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Column 1 */}
                      <Grid size={{ xs: 12, lg: 4 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Active Military</FormLabel>
                              <RadioGroup
                                row
                                name="activeMilitary"
                                value={values.activeMilitary || 'No'}
                                onChange={handleChange}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">MCO/Title</FormLabel>
                              <RadioGroup
                                row
                                name="mcoTitle"
                                value={values.mcoTitle || 'Title'}
                                onChange={handleChange}
                              >
                                <FormControlLabel value="MCO" control={<Radio />} label="MCO" />
                                <FormControlLabel value="Title" control={<Radio />} label="Title" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Plate Transfer</FormLabel>
                              <RadioGroup
                                row
                                name="plateTransfer"
                                value={values.plateTransfer || 'Yes'}
                                onChange={handleChange}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="Title" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Column 2 */}
                      <Grid size={{ xs: 12, lg: 4 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              label="Plate Number"
                              name="plateNumber"
                              id="plateNumber"
                              value={values.plateNumber || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.plateNumber && Boolean(errors.plateNumber)}
                              helperText={touched.plateNumber && errors.plateNumber}
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              label="Plate Expiration"
                              name="plateExpiration"
                              id="plateExpiration"
                              value={values.plateExpiration || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.plateExpiration && Boolean(errors.plateExpiration)}
                              helperText={touched.plateExpiration && errors.plateExpiration}
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Plate Type</FormLabel>
                              <RadioGroup
                                row
                                name="plateType"
                                value={values.plateType || 'Yes'}
                                onChange={handleChange}
                              >
                                <FormControlLabel value="Yes" control={<Radio />} label="Regular" />
                                <FormControlLabel value="Commercial" control={<Radio />} label="Commercial (Non-IRP)" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Column 3 */}
                      <Grid size={{ xs: 12, lg: 4 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Lien</FormLabel>
                              <RadioGroup row name="lien" value={values.lien || 'No'} onChange={handleChange}>
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="tradeIn"
                                  id="tradeIn"
                                  checked={values.tradeIn || false}
                                  onChange={handleChange}
                                />
                              }
                              label="Trade In? - Yes"
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                              <InputLabel>Trade In Vehicle Type</InputLabel>
                              <Select
                                name="tradeInVehicleType"
                                id="tradeInVehicleType"
                                value={values.tradeInVehicleType || ''}
                                label="Trade In Vehicle Type"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <MenuItem value="">Choose</MenuItem>
                                <MenuItem value="Trade In Type Here">Trade In Type Here</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                              <InputLabel>Trade In Vehicle Sub Type</InputLabel>
                              <Select
                                name="tradeInVehicleSubType"
                                id="tradeInVehicleSubType"
                                value={values.tradeInVehicleSubType || ''}
                                label="Trade In Vehicle Sub Type"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <MenuItem value="">Choose</MenuItem>
                                <MenuItem value="Trade In Sub Type Here">Trade In Sub Type Here</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              label="Trade In Model Year"
                              name="tradeInModelYear"
                              id="tradeInModelYear"
                              value={values.tradeInModelYear || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.tradeInModelYear && Boolean(errors.tradeInModelYear)}
                              helperText={touched.tradeInModelYear && errors.tradeInModelYear}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Taxable Fees Info Section - row-based grid so columns 2 & 3 align left */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Taxable Fees Info
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Purchase Date"
                          name="purchaseDate"
                          id="purchaseDate"
                          type="date"
                          value={values.purchaseDate || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.purchaseDate && Boolean(errors.purchaseDate)}
                          helperText={touched.purchaseDate && errors.purchaseDate}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Doc Fees"
                          name="docFees"
                          id="docFees"
                          value={values.docFees || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.docFees && Boolean(errors.docFees)}
                          helperText={touched.docFees && errors.docFees}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Dealer Inventory Taxes"
                          name="dealerInventoryTaxes"
                          id="dealerInventoryTaxes"
                          value={values.dealerInventoryTaxes || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.dealerInventoryTaxes && Boolean(errors.dealerInventoryTaxes)}
                          helperText={touched.dealerInventoryTaxes && errors.dealerInventoryTaxes}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Base Price"
                          name="basePrice"
                          id="basePrice"
                          value={values.basePrice || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.basePrice && Boolean(errors.basePrice)}
                          helperText={touched.basePrice && errors.basePrice}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Extended Warranties"
                          name="extendedWarranties"
                          id="extendedWarranties"
                          value={values.extendedWarranties || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.extendedWarranties && Boolean(errors.extendedWarranties)}
                          helperText={touched.extendedWarranties && errors.extendedWarranties}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Delivery Fees"
                          name="deliveryFees"
                          id="deliveryFees"
                          value={values.deliveryFees || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.deliveryFees && Boolean(errors.deliveryFees)}
                          helperText={touched.deliveryFees && errors.deliveryFees}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Trade In Value"
                          name="tradeInValue"
                          id="tradeInValue"
                          value={values.tradeInValue || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.tradeInValue && Boolean(errors.tradeInValue)}
                          helperText={touched.tradeInValue && errors.tradeInValue}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Add On's"
                          name="addOns"
                          id="addOns"
                          value={values.addOns || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.addOns && Boolean(errors.addOns)}
                          helperText={touched.addOns && errors.addOns}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Taxes Paid to Dealer State"
                          name="taxesPaidDealerState"
                          id="taxesPaidDealerState"
                          value={values.taxesPaidDealerState || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.taxesPaidDealerState && Boolean(errors.taxesPaidDealerState)}
                          helperText={touched.taxesPaidDealerState && errors.taxesPaidDealerState}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Protection Packages"
                          name="protectionPackages"
                          id="protectionPackages"
                          value={values.protectionPackages || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.protectionPackages && Boolean(errors.protectionPackages)}
                          helperText={touched.protectionPackages && errors.protectionPackages}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Dealer Service Fee"
                          name="dealerServiceFee"
                          id="dealerServiceFee"
                          value={values.dealerServiceFee || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.dealerServiceFee && Boolean(errors.dealerServiceFee)}
                          helperText={touched.dealerServiceFee && errors.dealerServiceFee}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Applicable Fees */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Applicable Fees
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {(() => {
                      const results = calculateFees(
                        states.find((st) => st.abbreviation === values.titleState),
                        values,
                      );
                      if (results.length === 0) {
                        return (
                          <Typography color="text.secondary">
                            Select a state to see the fees that apply to this quote.
                          </Typography>
                        );
                      }
                      const total = results.reduce((sum, r) => sum + r.amount, 0);
                      return (
                        <>
                          {results.map(({ fee, amount }) => (
                            <Box key={fee.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                              <Box>
                                <Typography variant="body2">{fee.name}</Typography>
                                {fee.conditions?.length ? (
                                  <Typography variant="caption" color="text.secondary">
                                    If {describeConditions(fee.conditions)}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Typography variant="body2">{formatMoney(amount)}</Typography>
                            </Box>
                          ))}
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 600 }}>Total</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{formatMoney(total)}</Typography>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>

                  {/* Submit Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }} className="no-print">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading || statesLoading || states.length === 0}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      onClick={() =>
                        navigate(`/quotes/${quoteId ?? 'new'}/preview`, {
                          state: {
                            quote: {
                              ...normalizeQuoteValues(values),
                              fees: snapshotFees(
                                states.find((st) => st.abbreviation === values.titleState),
                                values,
                              ),
                            },
                          },
                        })
                      }
                      variant="outlined"
                      size="large"
                      startIcon={<DescriptionIcon />}
                    >
                      Generate Quote
                    </Button>
                    <Button variant="outlined" size="large" onClick={() => navigate('/quotes')}>
                      Cancel
                    </Button>
                  </Box>

                  {isDev ? (
                    <>
                      <div>
                        <p>Values:</p>
                        <code>{JSON.stringify(values, null, 2)}</code>
                      </div>
                      <div>
                        <p>Errors:</p>
                        <code>{JSON.stringify(errors, null, 2)}</code>
                      </div>
                    </>
                  ) : null}
                  </Box>
                  {showActivityTabWithPermission && tabValue === 1 ? (
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        Audit log
                      </Typography>
                      {auditLogLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : auditLog.length === 0 ? (
                        <Typography color="text.secondary">No activity yet.</Typography>
                      ) : (
                        <List dense disablePadding>
                          {auditLog.map((entry, idx) => (
                            <ListItem
                              key={`${entry.timestamp}-${idx}`}
                              alignItems="flex-start"
                              disableGutters
                              sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                            >
                              <ListItemText
                                primary={auditEntrySummary(entry)}
                                secondary={auditEntryChanges(entry)}
                                slotProps={{ primary: { variant: 'body2' } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  ) : null}
                </Form>
              </>
            );
          }}
        </Formik>
      </Paper>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
